// src/supabaseClient.js
//
// Wave 3b (part 1) — NEW, ADDITIVE FILE. Not imported by any existing
// Firebase code path yet (App.js / ClientDashboard.js / firebase.js / ...
// all remain 100% untouched and still run entirely on Firestore/Firebase
// Auth/Cloud Functions). This module is the future replacement client,
// built ahead of the actual cutover so the wiring work is already done
// once the owner flips the two blockers documented below.
//
// ⚠️ TWO BLOCKERS MUST BE CLEARED BEFORE THIS CLIENT CAN DO ANYTHING LIVE
// (both documented in DAF_CONFIRMATION_CONTEXT.md → "Wave 3: RLS حقيقية +
// GRANTs + Realtime + خطة GoTrue"):
//
//   1. PostgREST schema exposure — dafinv-rest (the Supabase stack this
//      dashboard will read from) currently has
//        PGRST_DB_SCHEMAS=public,storage,graphql_public,daf_inventory
//      i.e. `daf_confirmation` is NOT exposed over PostgREST/Kong at all.
//      Every call made through the `supabase` client below (reads, writes,
//      RPCs, Realtime) will 404 until an owner adds `daf_confirmation` to
//      that env var and restarts the `dafinv-rest` container. See
//      `daf inventory/supabase/daf_confirmation_postgrest_plan.md` for the
//      exact steps + a live risk assessment (rated low-risk there).
//
//   2. GoTrue user provisioning — every real `clients_api` row's
//      `data.auth_uid` is currently a FIREBASE uid (28-char string), not a
//      GoTrue uuid. `auth.uid()` in Postgres always casts to `::uuid`, so
//      it can never equal a Firebase uid — meaning the RLS policies are
//      default-deny for every real brand until
//      `daf inventory/supabase/provision_confirmation_gotrue.mjs --live`
//      (dry-run only so far) is run and each row's `auth_uid` is rewritten
//      to that brand's new GoTrue user id.
//
// Until both are done, this file is correct-but-inert: it will build, it
// will type-check/parse cleanly, and the moment the two blockers above are
// cleared, every function in src/lib/supabaseAuth.js and
// src/lib/confirmationData.js that imports `supabase` from here starts
// working with ZERO code changes.
//
// Required env vars (owner must add — Create React App only inlines
// variables prefixed with REACT_APP_ at build time, see
// https://create-react-app.dev/docs/adding-custom-environment-variables/):
//
//   REACT_APP_SUPABASE_URL       e.g. https://supabase-daf-inventory.dafaiagency.shop
//   REACT_APP_SUPABASE_ANON_KEY  the `dafinv` stack's ANON key (never the
//                                 service_role key — that one must never
//                                 reach the browser bundle)
//
// Put them in `.env.local` (already gitignored in this repo, see
// .gitignore) — NOT committed, matching how the existing Firebase project
// keeps `firebase-key.json`/service-account style secrets out of git in
// the sibling Shopify-app project. Reference values for this stack already
// exist (same Supabase project as DAF Inventory) in
// `daf inventory/dashboard/.env.local` (`SUPABASE_URL` / `SUPABASE_ANON_KEY`)
// and/or `daf inventory/supabase/.env` (`ANON_KEY`) — copy the values from
// there, do not regenerate new ones.

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Don't throw at import time. Several existing pages in this dashboard
// degrade gracefully instead of crashing on missing config (e.g.
// BostaPage.js shows an inline error message rather than throwing), and a
// hard throw here would break `npm run build`/any component that merely
// imports this module before the owner has populated `.env.local`. A
// console warning matches that convention.
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    '[supabaseClient] REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY ' +
    'are not set. Every supabase-js call (auth, data, realtime) will fail ' +
    'until they are added to the dashboard .env.local — see the header ' +
    'comment in src/supabaseClient.js and src/lib/MIGRATION_MAP.md ("Env ' +
    'vars needed from the owner").'
  );
}

export const supabase = createClient(
  SUPABASE_URL || 'https://placeholder.invalid',
  SUPABASE_ANON_KEY || 'placeholder-anon-key',
  {
    db: {
      // Every table this dashboard talks to (clients_api, pending_orders_api,
      // final_orders_api, chat_histories_api, feedbacks_api,
      // knowledge_base_api, non_ai_chat_api, brand_questions_api,
      // brand_stats_api, bosta_api) lives in this schema — see
      // daf_confirmation_rls.sql. Setting it here means every call site in
      // src/lib/confirmationData.js can just say
      // `supabase.from('clients_api')` without repeating `.schema(...)`.
      schema: 'daf_confirmation',
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // This dashboard is not an OAuth-redirect consumer (merchants sign in
      // with email+password via GoTrue's password grant, not a magic-link/
      // OAuth redirect) — no need to parse the URL fragment on load.
      detectSessionInUrl: false,
      // Isolated storage key so this client's session never collides with
      // any other supabase-js instance that might one day run in the same
      // browser (e.g. if a merchant ever has both this dashboard and the
      // DAF Inventory/DAF One dashboards open on the same domain/browser
      // profile — those are separate GoTrue user pools in the same
      // physical Postgres, but keeping storage keys distinct is a cheap,
      // defensive habit).
      storageKey: 'daf-confirmation-auth',
    },
    realtime: {
      params: { eventsPerSecond: 10 },
    },
  }
);

export default supabase;
